/* ════════════ THE CONVEYOR ════════════
   The amazon page's atmosphere: parcels glide along faint conveyor
   lines. Every so often a scan-line sweeps one and it flashes orange
   with a little barcode, then carries on. Everything gets scanned.
   The parcel doesn't get a say. Neither did you. */

import { onPage } from "./router";

const REDUCED = matchMedia("(prefers-reduced-motion: reduce)").matches;

function conveyor(signal: AbortSignal) {
  const c = document.querySelector<HTMLCanvasElement>("#conveyor")!;
  const ctx = c.getContext("2d")!;

  const ORANGE = "#ff9900";
  const GREY = "#9aa0a6";

  type Parcel = {
    x: number;
    lane: number; // index into lanes[]
    w: number;
    h: number;
    v: number; // px per frame, signed (belt direction)
    scan: number; // frames remaining in the scan flash, 0 = idle
    next: number; // frames until the next scan
    code: number[]; // barcode stripe widths, drawn while scanned
  };
  let lanes: number[] = [];
  let parcels: Parcel[] = [];

  const barcode = () => Array.from({ length: 6 }, () => 1 + Math.floor(Math.random() * 2));

  const spawn = (lane: number, anywhere: boolean): Parcel => {
    const v = (lane % 2 === 0 ? 1 : -1) * (0.25 + Math.random() * 0.35);
    const w = 16 + Math.random() * 18;
    return {
      x: anywhere ? Math.random() * c.width : v > 0 ? -w : c.width + w,
      lane,
      w,
      h: 10 + Math.random() * 8,
      v,
      scan: 0,
      next: 240 + Math.random() * 600,
      code: barcode(),
    };
  };

  const seed = () => {
    c.width = innerWidth;
    c.height = innerHeight;
    const laneCount = Math.max(3, Math.min(7, Math.floor(c.height / 160)));
    lanes = Array.from({ length: laneCount }, (_, i) => ((i + 0.7) / (laneCount + 0.4)) * c.height);
    const count = Math.min(22, Math.floor((c.width * c.height) / 80000));
    parcels = Array.from({ length: count }, (_, i) => spawn(i % laneCount, true));
  };
  seed();
  addEventListener("resize", seed, { signal });

  const draw = () => {
    ctx.clearRect(0, 0, c.width, c.height);

    // the belts: faint dashed lines, always running
    ctx.globalAlpha = 0.07;
    ctx.strokeStyle = GREY;
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 9]);
    for (const y of lanes) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(c.width, y);
      ctx.stroke();
    }
    ctx.setLineDash([]);

    for (const p of parcels) {
      const y = lanes[p.lane]! - p.h - 1; // box rides on the belt
      const hot = p.scan > 0;
      ctx.globalAlpha = hot ? 0.45 : 0.18;
      ctx.strokeStyle = hot ? ORANGE : GREY;
      ctx.lineWidth = 1;
      // the parcel, with its tape line down the middle
      ctx.strokeRect(p.x, y, p.w, p.h);
      ctx.beginPath();
      ctx.moveTo(p.x + p.w / 2, y);
      ctx.lineTo(p.x + p.w / 2, y + p.h);
      ctx.stroke();
      if (hot) {
        // the scan-line sweeps the box…
        const sweep = 1 - p.scan / 40;
        ctx.beginPath();
        ctx.moveTo(p.x + p.w * sweep, y - 4);
        ctx.lineTo(p.x + p.w * sweep, y + p.h + 4);
        ctx.stroke();
        // …and the barcode it found
        ctx.globalAlpha = 0.35;
        let bx = p.x + 3;
        for (const wdt of p.code) {
          ctx.fillStyle = ORANGE;
          ctx.fillRect(bx, y + p.h + 4, wdt, 4);
          bx += wdt + 2;
        }
      }
    }
    ctx.globalAlpha = 1;
  };

  if (REDUCED) {
    draw();
    return;
  }
  const tick = () => {
    if (signal.aborted) return;
    for (let i = 0; i < parcels.length; i++) {
      const p = parcels[i]!;
      p.x += p.v;
      if (p.scan > 0) p.scan--;
      else if (--p.next <= 0) {
        p.scan = 40;
        p.next = 240 + Math.random() * 600;
        p.code = barcode();
      }
      if ((p.v > 0 && p.x > c.width + p.w) || (p.v < 0 && p.x < -p.w * 2)) {
        parcels[i] = spawn(p.lane, false);
      }
    }
    draw();
    requestAnimationFrame(tick);
  };
  tick();
}

onPage(conveyor);
